import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { InventoryToolbar } from "@/components/inventory/InventoryToolbar";
import { InventoryTable } from "@/components/inventory/InventoryTable";
import { ItemFormDialog, QuickEditDialog, NotesDialog, LedgerDialog, ImageDialog, BulkAddDialog } from "@/components/inventory/ItemDialogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Inventory = () => {
  const { user, hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(100);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [quickEditDialog, setQuickEditDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [notesDialog, setNotesDialog] = useState(false);
  const [ledgerDialog, setLedgerDialog] = useState(false);
  const [imageDialog, setImageDialog] = useState(false);
  const [bulkAddDialog, setBulkAddDialog] = useState(false);
  const [bulkUploadDialog, setBulkUploadDialog] = useState(false);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [quickEditItem, setQuickEditItem] = useState(null);
  const [quickQuantity, setQuickQuantity] = useState(0);
  const [transactionType, setTransactionType] = useState("sale");
  const [importFile, setImportFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [bulkItems, setBulkItems] = useState([
    { part_number: "", name: "", category: "", machine_model: "", brand: "", location: "", weight: 0, quantity: 0, reorder_level: 10, notes: "" }
  ]);
  const [bulkRowErrors, setBulkRowErrors] = useState({});
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "", part_number: "", quantity: 0, reorder_level: 10, category: "", machine_model: "", location: "", brand: "", weight: 0, image_url: "", notes: "",
  });

  const canEdit = hasPerm("inventory_edit") || hasPerm("inventory_create");
  const canDelete = hasPerm("inventory_delete");
  const canDownload = hasPerm("downloads_allowed");
  const isAdmin = user?.role === "admin";

  useEffect(() => { fetchInventory(); }, [currentPage]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (currentPage !== 1) setCurrentPage(1);
      else fetchInventory();
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchInventory = async () => {
    try {
      const params = new URLSearchParams({ page: currentPage, page_size: pageSize });
      if (searchTerm) params.append('search', searchTerm);
      const response = await axios.get(`${API_URL}/inventory?${params}`);
      const data = response.data;
      setItems(data.items || []);
      setFilteredItems(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotalItems(data.total || 0);
    } catch (error) {
      toast.error("Failed to fetch inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/inventory/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return response.data.image_url;
    } catch (error) {
      toast.error("Failed to upload image");
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = formData.image_url;
      if (imageFile) {
        imageUrl = await handleImageUpload(imageFile);
        if (!imageUrl) return;
      }
      const submitData = { ...formData, image_url: imageUrl };
      if (editingItem) {
        await axios.put(`${API_URL}/inventory/${editingItem.id}`, submitData);
        toast.success("Item updated successfully");
      } else {
        await axios.post(`${API_URL}/inventory`, submitData);
        toast.success("Item added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`${API_URL}/inventory/${id}`);
      toast.success("Item deleted successfully");
      fetchInventory();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name, part_number: item.part_number, quantity: item.quantity, reorder_level: item.reorder_level,
      category: item.category || "", machine_model: item.machine_model || "", location: item.location || "",
      brand: item.brand || "", weight: item.weight || 0, image_url: item.image_url || "", notes: item.notes || "",
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setImageFile(null);
    setFormData({ name: "", part_number: "", quantity: 0, reorder_level: 10, category: "", machine_model: "", location: "", brand: "", weight: 0, image_url: "", notes: "" });
  };

  const openAddDialog = () => { resetForm(); setDialogOpen(true); };

  const handleDialogClose = (open) => { setDialogOpen(open); if (!open) resetForm(); };

  const viewNotes = (item) => { setSelectedItem(item); setNotesDialog(true); };
  const viewImage = (item) => { setSelectedItem(item); setImageDialog(true); };
  const viewLedger = async (item) => {
    setSelectedItem(item);
    try {
      const response = await axios.get(`${API_URL}/inventory/${item.id}/ledger`);
      setLedgerData(response.data);
      setLedgerDialog(true);
    } catch (error) {
      toast.error("Failed to fetch ledger");
    }
  };

  const openQuickEdit = (item) => { setQuickEditItem(item); setQuickQuantity(item.quantity); setTransactionType("sale"); setQuickEditDialog(true); };

  const handleQuickUpdate = async () => {
    try {
      await axios.post(`${API_URL}/inventory/${quickEditItem.id}/quick-update`, { quantity: quickQuantity, transaction_type: transactionType });
      toast.success("Quantity updated successfully");
      setQuickEditDialog(false);
      fetchInventory();
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'inventory_export.csv');
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("CSV exported successfully");
    } catch (error) { toast.error("Failed to export CSV"); }
  };

  const handleExportLowStockCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/export/low-stock-csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'low_stock_alert.csv');
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Low stock CSV exported successfully");
    } catch (error) { toast.error("Failed to export low stock CSV"); }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/export/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'inventory_export.pdf');
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("PDF exported successfully");
    } catch (error) { toast.error("Failed to export PDF"); }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'inventory_export.xlsx');
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Excel exported successfully");
    } catch (error) { toast.error("Failed to export Excel"); }
  };

  const handleImportExcel = async () => {
    if (!importFile) { toast.error("Please select a file"); return; }
    const fd = new FormData(); fd.append('file', importFile);
    try {
      const response = await axios.post(`${API_URL}/inventory/import/excel`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.error_count > 0) toast.warning(`Import completed with ${response.data.success_count} success and ${response.data.error_count} errors`);
      else toast.success(`Successfully imported ${response.data.success_count} items`);
      setImportDialog(false); setImportFile(null); fetchInventory();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to import Excel"); }
  };

  const handleImportCSV = async () => {
    if (!importFile) { toast.error("Please select a file"); return; }
    const fd = new FormData(); fd.append('file', importFile);
    try {
      const response = await axios.post(`${API_URL}/inventory/import/csv`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.error_count > 0) toast.warning(`Import completed with ${response.data.success_count} success and ${response.data.error_count} errors`);
      else toast.success(`Successfully imported ${response.data.success_count} items`);
      setImportDialog(false); setImportFile(null); fetchInventory();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to import CSV"); }
  };

  const addBulkRow = () => setBulkItems([...bulkItems, { part_number: "", name: "", category: "", machine_model: "", brand: "", location: "", weight: 0, quantity: 0, reorder_level: 10, notes: "" }]);
  const removeBulkRow = (index) => { if (bulkItems.length > 1) setBulkItems(bulkItems.filter((_, i) => i !== index)); };
  const updateBulkItem = (index, field, value) => { const updated = [...bulkItems]; updated[index][field] = value; setBulkItems(updated); };

  const handleBulkSubmit = async () => {
    const validItems = bulkItems.filter(item => item.part_number && item.name);
    if (validItems.length === 0) { toast.error("Please fill in at least one item with Part Number and Name"); return; }
    setBulkSubmitting(true);
    setBulkRowErrors({});
    try {
      const response = await axios.post(`${API_URL}/inventory/bulk`, validItems);
      if (response.data.error_count > 0) {
        // Build row-error map from structured errors
        const errorMap = {};
        (response.data.errors || []).forEach(err => {
          if (typeof err === 'object' && err.row !== undefined) {
            errorMap[err.row] = err.messages || [JSON.stringify(err)];
          }
        });
        setBulkRowErrors(errorMap);
        toast.warning(`Added ${response.data.success_count} items, ${response.data.error_count} failed — see highlighted rows`);
        // Remove successfully added items, keep only error rows
        const errorIndices = new Set(Object.keys(errorMap).map(Number));
        const remaining = bulkItems.filter((_, i) => errorIndices.has(i));
        if (remaining.length > 0) {
          setBulkItems(remaining);
          // Re-index errors starting from 0
          const reindexed = {};
          let newIdx = 0;
          Object.keys(errorMap).sort((a, b) => a - b).forEach(oldIdx => {
            reindexed[newIdx] = errorMap[oldIdx];
            newIdx++;
          });
          setBulkRowErrors(reindexed);
        }
        fetchInventory();
      } else {
        toast.success(`Successfully added ${response.data.success_count} items`);
        setBulkAddDialog(false);
        setBulkItems([{ part_number: "", name: "", brand: "", location: "", weight: 0, quantity: 0, reorder_level: 10, notes: "" }]);
        setBulkRowErrors({});
        fetchInventory();
      }
    } catch (error) { toast.error("Failed to add items"); }
    finally { setBulkSubmitting(false); }
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) { toast.error("Select a file"); return; }
    setBulkUploading(true); setBulkUploadResult(null);
    const fd = new FormData(); fd.append('file', bulkUploadFile);
    try {
      const response = await axios.post(`${API_URL}/inventory/bulk-upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      setBulkUploadResult(response.data);
      if (response.data.error_count === 0) {
        toast.success(`Uploaded ${response.data.success_count} items (${response.data.new_items} new, ${response.data.updates} updated)`);
      } else {
        toast.warning(`${response.data.success_count} success, ${response.data.error_count} errors`);
      }
      fetchInventory();
    } catch (error) { toast.error(error.response?.data?.detail || "Upload failed"); }
    finally { setBulkUploading(false); }
  };

  const handleBulkDelete = async () => {
    try {
      const response = await axios.post(`${API_URL}/inventory/bulk-delete`, { confirm_password: deletePassword, confirm_text: deleteConfirmText });
      toast.success(response.data.message);
      setBulkDeleteDialog(false); setDeleteConfirmText(""); setDeletePassword("");
      fetchInventory();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete"); }
  };

  if (loading) {
    return <div className="p-8"><div className="text-slate-500">Loading inventory...</div></div>;
  }

  return (
    <div className="p-4 sm:p-6 md:p-8" data-testid="inventory-page">
      <InventoryToolbar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} canEdit={canEdit} canDownload={canDownload}
        onExportCSV={handleExportCSV} onExportLowStockCSV={handleExportLowStockCSV} onExportPDF={handleExportPDF}
        onExportExcel={handleExportExcel} onImportExcel={handleImportExcel}
        importDialog={importDialog} onImportDialogChange={setImportDialog}
        importFile={importFile} onImportFileChange={setImportFile} onImportCSV={handleImportCSV}
        onBulkAdd={() => setBulkAddDialog(true)} onAddItem={openAddDialog}
        onBulkUpload={() => setBulkUploadDialog(true)} onBulkDelete={isAdmin ? () => setBulkDeleteDialog(true) : null}
        totalItems={totalItems}
      />

      <InventoryTable
        items={filteredItems} canEdit={canEdit} canDelete={canDelete}
        onViewNotes={viewNotes} onViewImage={viewImage} onViewLedger={viewLedger}
        onQuickEdit={openQuickEdit} onEdit={openEditDialog} onDelete={handleDelete}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 bg-white border border-slate-200 rounded-sm p-3 gap-2">
          <p className="text-sm text-slate-500">Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalItems)} of {totalItems} items</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)} data-testid="prev-page">Previous</Button>
            <span className="text-sm text-slate-700 px-2">Page {currentPage} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)} data-testid="next-page">Next</Button>
          </div>
        </div>
      )}

      <ItemFormDialog
        open={dialogOpen} onOpenChange={handleDialogClose} editingItem={editingItem}
        formData={formData} imageFile={imageFile} onFormChange={setFormData}
        onImageChange={setImageFile} onSubmit={handleSubmit}
      />

      <QuickEditDialog
        open={quickEditDialog} onOpenChange={setQuickEditDialog} item={quickEditItem}
        quantity={quickQuantity} transactionType={transactionType}
        onQuantityChange={setQuickQuantity} onTypeChange={setTransactionType} onSubmit={handleQuickUpdate}
      />

      <NotesDialog open={notesDialog} onOpenChange={setNotesDialog} item={selectedItem} />
      <LedgerDialog open={ledgerDialog} onOpenChange={setLedgerDialog} item={selectedItem} data={ledgerData} />
      <ImageDialog open={imageDialog} onOpenChange={setImageDialog} item={selectedItem} />

      <BulkAddDialog
        open={bulkAddDialog} onOpenChange={(open) => { setBulkAddDialog(open); if (!open) { setBulkRowErrors({}); setBulkSubmitting(false); } }}
        items={bulkItems}
        onAddRow={addBulkRow} onRemoveRow={removeBulkRow}
        onUpdateItem={updateBulkItem} onSubmit={handleBulkSubmit} onSetItems={setBulkItems}
        rowErrors={bulkRowErrors} submitting={bulkSubmitting}
      />

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadDialog} onOpenChange={setBulkUploadDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle className="text-xl font-bold uppercase">Bulk Upload (30K+ Items)</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
              <p className="text-sm text-blue-800">Upload Excel/CSV with columns: <span className="font-mono text-xs">Part Number, Name, Brand, Category, Location, Quantity, Reorder Level</span></p>
              <p className="text-xs text-blue-600 mt-1">Existing items (by Part Number) will be updated. New items will be created.</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-600">Excel or CSV File</Label>
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { setBulkUploadFile(e.target.files[0]); setBulkUploadResult(null); }} className="mt-1 rounded-sm" data-testid="bulk-upload-file" />
            </div>
            {bulkUploading && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-sm">
                <p className="text-sm text-orange-800 font-medium">Processing... please wait</p>
                <div className="w-full bg-orange-200 rounded-full h-2 mt-2"><div className="bg-orange-500 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div></div>
              </div>
            )}
            {bulkUploadResult && (
              <div data-testid="bulk-upload-result">
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-sm text-center">
                    <p className="text-2xl font-bold text-green-700">{bulkUploadResult.success_count}</p>
                    <p className="text-xs text-green-600 font-medium uppercase">Successful</p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm text-center">
                    <p className="text-2xl font-bold text-blue-700">{bulkUploadResult.new_items || 0} / {bulkUploadResult.updates || 0}</p>
                    <p className="text-xs text-blue-600 font-medium uppercase">New / Updated</p>
                  </div>
                  <div className={`p-3 rounded-sm text-center border ${bulkUploadResult.error_count > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                    <p className={`text-2xl font-bold ${bulkUploadResult.error_count > 0 ? 'text-red-700' : 'text-slate-400'}`}>{bulkUploadResult.error_count}</p>
                    <p className={`text-xs font-medium uppercase ${bulkUploadResult.error_count > 0 ? 'text-red-600' : 'text-slate-400'}`}>Errors</p>
                  </div>
                </div>
                {/* Error Table */}
                {bulkUploadResult.error_count > 0 && bulkUploadResult.errors && bulkUploadResult.errors.length > 0 && (
                  <div className="border border-red-200 rounded-sm overflow-hidden" data-testid="bulk-upload-error-table">
                    <div className="bg-red-100 px-3 py-2 flex items-center justify-between">
                      <p className="text-xs font-bold text-red-800 uppercase">Error Details ({bulkUploadResult.errors.length} rows)</p>
                      <Button
                        size="sm" variant="ghost"
                        className="text-xs text-red-700 hover:text-red-900 h-7 px-2"
                        data-testid="download-error-report"
                        onClick={() => {
                          const csvRows = ["Row,Part Number,Name,Error"];
                          bulkUploadResult.errors.forEach(err => {
                            const row = err.row || '-';
                            const pn = err.data?.['Part Number'] || err.data?.part_number || err.data?.[Object.keys(err.data || {})[0]] || '-';
                            const nm = err.data?.['Name'] || err.data?.name || err.data?.[Object.keys(err.data || {})[1]] || '-';
                            const msgs = (err.errors || [err]).join('; ');
                            csvRows.push(`${row},"${pn}","${nm}","${msgs}"`);
                          });
                          const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a'); a.href = url; a.download = 'upload_errors.csv';
                          document.body.appendChild(a); a.click(); a.remove();
                        }}
                      >
                        Download CSV
                      </Button>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-red-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-1.5 font-bold text-red-700 w-12">Row</th>
                            <th className="text-left px-3 py-1.5 font-bold text-red-700">Part Number</th>
                            <th className="text-left px-3 py-1.5 font-bold text-red-700">Name</th>
                            <th className="text-left px-3 py-1.5 font-bold text-red-700">Error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-red-100">
                          {bulkUploadResult.errors.map((err, i) => {
                            const pn = err.data?.['Part Number'] || err.data?.part_number || err.data?.[Object.keys(err.data || {})[0]] || '-';
                            const nm = err.data?.['Name'] || err.data?.name || err.data?.[Object.keys(err.data || {})[1]] || '-';
                            return (
                              <tr key={i} className="bg-red-50 hover:bg-red-100" data-testid={`upload-error-row-${i}`}>
                                <td className="px-3 py-1.5 text-red-800 font-mono font-bold">{err.row || '-'}</td>
                                <td className="px-3 py-1.5 text-red-700 font-medium truncate max-w-[120px]">{pn}</td>
                                <td className="px-3 py-1.5 text-red-700 truncate max-w-[120px]">{nm}</td>
                                <td className="px-3 py-1.5 text-red-600">{(err.errors || []).join(', ')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkUploadDialog(false)}>Close</Button>
              <Button onClick={handleBulkUpload} disabled={!bulkUploadFile || bulkUploading} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="start-bulk-upload">
                {bulkUploading ? "Processing..." : "Upload & Process"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      {isAdmin && (
        <Dialog open={bulkDeleteDialog} onOpenChange={setBulkDeleteDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="text-xl font-bold uppercase text-red-600">Delete All Inventory</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
                <p className="text-sm text-red-800 font-bold">WARNING: This will permanently delete ALL {totalItems} inventory items. This action cannot be undone.</p>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-600">Type "DELETE ALL" to confirm</Label>
                <Input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder='Type "DELETE ALL"' className="mt-1 rounded-sm" data-testid="bulk-delete-confirm-text" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-600">Enter your password</Label>
                <Input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} className="mt-1 rounded-sm" data-testid="bulk-delete-password" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkDeleteDialog(false)}>Cancel</Button>
                <Button variant="destructive" onClick={handleBulkDelete} disabled={deleteConfirmText !== "DELETE ALL" || !deletePassword} data-testid="confirm-bulk-delete">Delete All Items</Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Inventory;
